import { isoDate } from '../../lib/format'
import {
  haversineM,
  mergeTrackParts,
  nachFahrtzeit,
  trackKonflikt,
  trackParts,
  tracksByDay,
} from '../../lib/gpx'
import { halteUnterwegs } from '../../lib/fahrzeit'
import { newId } from '../../lib/id'
import { MAX_VIA } from '../../lib/route'
import type {
  DiaryEntry,
  DiaryTrack,
  LatLon,
  Stage,
  TrackPart,
  TrackStop,
  Waypoint,
} from '../../lib/types'
import { leseDateien, warnungenAus, type Lesbar, type Lesefehler } from './mehrdateien'

/**
 * Aus einer Aufzeichnung Etappen entstehen lassen.
 *
 * Die App ist gebaut für: erst planen, dann fahren, dann dokumentieren. Der
 * Fall, für den diese Datei da ist, ist der andere - gefahren und
 * aufgezeichnet, aber nichts geplant. Er ist kein Sonderfall: auf einer Reise
 * über sechzehn Etappen ist das spontane Abweichen der Normalfall, und abends
 * will man festhalten, was wirklich war.
 *
 * Bis hierher konnte die App aus einer Aufzeichnung nur Bestehendes füllen.
 * features/diary/verteilen.ts verteilt eine mehrtägige Datei auf **bestehende**
 * Etappen; gibt es keine, gibt es nichts zu verteilen. Hier entstehen sie.
 *
 * Reine Funktionen, kein React: was eine ganze Reise auf einmal anlegt, gehört
 * unter Test. Die Bedienung dazu steht in EtappenAusAufzeichnung.tsx, und sie
 * schreibt nichts, bevor der Nutzer nicht gesehen hat, was entsteht.
 *
 * ## Was eine Etappe wird
 *
 * Ein Kalendertag, und nicht ein Abschnitt zwischen zwei langen Halten.
 *
 * Beides wäre vertretbar, aber nur eines passt zum Modell dieser App: „Eine
 * Etappe ist ein Tag - so ist die Planung angelegt“ (lib/calendar.ts). Daran
 * hängt der Reisetag, an ihm das Datum jeder Etappe, an ihm der Zeitraum auf
 * dem Umschlag des gedruckten Buchs. Zwei Etappen mit demselben Datum sind in
 * diesem Modell bereits ein Sonderfall, den stagesByDate ausdrücklich behandeln
 * muss - sie zum Regelfall zu machen, hiesse den Kalender der Reise aufzugeben.
 *
 * Der gemessene Tag zeigt, was daran hängt: vier Aufzeichnungen, ein
 * Arbeitstag, vier Orte. Als Abschnitte wären das vier Etappen an einem Tag mit
 * drei künstlichen Übernachtungen dazwischen. Als Tag ist es das, was es war:
 * eine Fahrt von zu Hause nach Hause über Zofingen, Olten und Aarau.
 *
 * ## Die Kette
 *
 * Das Ziel einer Etappe ist der Start der nächsten - das ist die Regel dieses
 * Datenmodells, und was hier entsteht, erfüllt sie von sich aus: die Kette wird
 * aus einer einzigen Liste von Punkten gebaut, jeder Punkt ist Ziel des einen
 * und Start des nächsten Tages. Es gibt keinen Weg, auf dem sie hier reissen
 * könnte, denn es gibt keine zweite Quelle für die Koordinaten.
 *
 * Wo die Aufzeichnung des nächsten Tages nicht genau dort anfängt, wo die des
 * vorigen aufgehört hat, gewinnt die Kette und die Abweichung wird angezeigt
 * (siehe FEHLSTUECK_AB_M). Angeglichen wird nichts still: an derselben Stelle
 * warnt die Etappenliste schon heute, wenn ein Start nicht zum Ziel davor passt
 * (startHerkunft in lib/trip.ts), und drücken muss dort ein Mensch.
 */

/**
 * Ab welchem Abstand zweier Aufzeichnungen ein Stück Weg fehlt.
 * Zweihundert Meter.
 *
 * Darunter ist es derselbe Ort. Ein Empfänger irrt sich um einige zehn Meter,
 * und das Fahrzeug steht am Morgen ein paar Wagenlängen anders auf dem Hof als
 * am Abend - die App rechnet an anderer Stelle mit vierzig Metern für das
 * blosse Rauschen im Stand (STOP_RADIUS_M in lib/gpx.ts). Zweihundert sind das
 * Fünffache davon: was darunter liegt, ist keine Auskunft, sondern eine
 * Belästigung. Wer bei jeder Ungenauigkeit gefragt wird, liest bald nichts mehr.
 *
 * Darüber fehlt wirklich ein Stück. Gemessen an den beiden Fällen, die
 * vorliegen: zwischen zwei Fahrtagen liegen am selben Haus 65 Meter, zwischen
 * der Einfahrt in ein Parkhaus und der Ausfahrt aus seiner zweiten Zufahrt 409.
 * Die Schwelle liegt mit reichlich Abstand zwischen beiden, und beide Zahlen
 * sind gemessen und nicht ausgedacht.
 *
 * Was ausdrücklich nicht in die Entscheidung eingeht, ist die verstrichene
 * Zeit. Sie sagt darüber nichts: vierhundert Meter in zwei Minuten und
 * dieselben vierhundert Meter in hundertzehn Minuten sind beide gefahren
 * worden - im zweiten Fall stand das Fahrzeug den Rest der Zeit, und wie lange
 * es stand, weiss keine dieser beiden Dateien.
 */
export const FEHLSTUECK_AB_M = 200

/**
 * Ab welcher Tagesstrecke eine Etappe als lange gilt. 250 Kilometer.
 *
 * Die Art einer Etappe steuert nur die Darstellung (StageType in types.ts), und
 * geraten wird hier nur, was ohne Schaden falsch sein darf. Ein Tag ohne
 * gefahrene Kilometer ist ein Standtag, ein langer Tag ist lang, alles
 * dazwischen kurz. Pass, Grenze und Heimweg stehen bewusst nicht zur Wahl: die
 * kann man einer Aufzeichnung nicht ansehen, und ein falsches Zeichen im Buch
 * ist schlechter als ein neutrales.
 */
export const LANGE_ETAPPE_KM = 250

/** Ein Stück Weg, das gefahren, aber nicht aufgezeichnet wurde. */
export interface Fehlstueck {
  /** Der letzte aufgezeichnete Punkt davor. */
  von: LatLon
  /** Der erste aufgezeichnete Punkt danach. */
  nach: LatLon
  /** Luftlinie dazwischen, in Metern. */
  meter: number
  /** Wann die Aufzeichnung abbricht, Millisekunden seit 1970. */
  ab: number
  /** Wann sie wieder anfängt. */
  bis: number
}

/** Ein Fahrtag, so wie er als Etappe entstehen würde. */
export interface Tagesentwurf {
  /** ISO-Datum des Tages. Leer, wenn die Dateien keine Uhr mitbringen. */
  datum: string
  /** Alles, was an diesem Tag aufgezeichnet wurde, zusammengelegt. */
  track: DiaryTrack
  /** Wo die Aufzeichnung des Tages anfängt. */
  von: LatLon
  /** Wo sie aufhört. */
  bis: LatLon
  /** Die Lücken zwischen den Aufzeichnungen dieses Tages. */
  fehlstuecke: Fehlstueck[]
  /** Die Halte unterwegs, ohne die an den Rändern (siehe halteUnterwegs). */
  halte: TrackStop[]
  /** Stücke, die schon in diesem Tag lagen und deshalb nicht zweimal zählen. */
  doppelt: number
  /** Ob dieser Tag mitgeht. */
  aktiv: boolean
}

/** Wie ein Punkt heisst und wo er liegt. */
export interface Ortsangabe {
  /** Der kurze Name, wie er als Start oder Ziel dransteht. Leer heisst unbekannt. */
  name: string
  /** Länderkürzel nach ISO 3166-1, gross geschrieben. Leer heisst unbekannt. */
  land: string
}

/** Was über die Punkte einer Kette bekannt ist, nach ortsSchluessel abgelegt. */
export type Ortsverzeichnis = Record<string, Ortsangabe>

/**
 * Der Schlüssel, unter dem ein Punkt in einem Ortsverzeichnis steht.
 *
 * Fünf Nachkommastellen sind etwa ein Meter - dieselbe Genauigkeit, mit der
 * Server und Zwischenspeicher rechnen (coordText in lib/geocode.ts). Zwei
 * Anfragen für denselben Punkt gibt es damit nicht: das Ziel des einen Tages
 * und der Start des nächsten sind derselbe Punkt und derselbe Schlüssel.
 */
export function ortsSchluessel([lat, lon]: LatLon): string {
  return `${lat.toFixed(5)},${lon.toFixed(5)}`
}

/** Der Punkt, an dem eine Geometrie anfängt beziehungsweise aufhört. */
function rand(geometry: LatLon[], ende: boolean): LatLon {
  const punkt = ende ? geometry[geometry.length - 1] : geometry[0]
  return punkt ? [punkt[0], punkt[1]] : [0, 0]
}

/**
 * Die Lücken zwischen den Teilen eines Tages.
 *
 * Angesehen wird der Abstand zwischen dem Ende des einen und dem Anfang des
 * nächsten Teils - genau die Naht, an der lib/gpx.ts entscheidet, ob dort
 * gestanden oder gefahren wurde (brueckeZwischen). Was diese Funktion findet,
 * ist deshalb keine zweite Rechnung, sondern die Auskunft darüber: an dieser
 * Naht sind Kilometer in die Summe gegangen, für die es keine Aufzeichnung
 * gibt.
 */
function fehlstueckeVon(teile: TrackPart[]): Fehlstueck[] {
  const out: Fehlstueck[] = []
  for (let i = 1; i < teile.length; i++) {
    const a = teile[i - 1]!
    const b = teile[i]!
    const von = rand(a.geometry, true)
    const nach = rand(b.geometry, false)
    const meter = Math.round(haversineM(von, nach))
    if (meter < FEHLSTUECK_AB_M) continue
    out.push({ von, nach, meter, ab: a.endedAt ?? 0, bis: b.startedAt ?? 0 })
  }
  return out
}

/**
 * Legt die Stücke aller gewählten Dateien zu Fahrtagen zusammen.
 *
 * Zerschnitten sind die Dateien schon (tracksByDay in lib/gpx.ts): an den
 * Pausen ab vier Stunden, nicht um Mitternacht. Hier wird die Gegenrichtung
 * gemacht - was auf denselben Kalendertag fällt, gehört zu derselben Etappe,
 * ganz gleich aus wie vielen Dateien es kommt. Der gemessene Tag besteht aus
 * vier Aufzeichnungen, weil das Gerät bei jedem Halt in Bereitschaft geht; das
 * ist eine Eigenschaft des Geräts und keine der Reise.
 *
 * Zusammengelegt wird mit mergeTrackParts, also mit derselben Rechnung wie beim
 * Nachtragen einer zweiten Datei auf einer bestehenden Etappe. Die Lücken
 * dazwischen werden dort gelesen wie jede andere Pause: stand das Fahrzeug, ist
 * es ein Halt, ging es weiter, zählt die Strecke.
 *
 * Dasselbe Stück zweimal zählt nicht zweimal: wer dieselbe Datei doppelt
 * auswählt, bekommt sie einmal und die Zahl der übersprungenen Stücke dazu
 * (trackKonflikt). Ohne das wären die Kilometer eines Tages doppelt, und sie
 * sähen genauso selbstbewusst aus wie richtige.
 *
 * @param tage Die Stücke aller gelesenen Dateien, in beliebiger Reihenfolge.
 * @returns Ein Entwurf je Kalendertag, nach Datum sortiert. Stücke ohne Uhr
 *   haben keinen Tag und stehen zusammen am Ende - verworfen wird nichts.
 */
export function tagesentwuerfe(tage: DiaryTrack[]): Tagesentwurf[] {
  const gruppen = new Map<string, TrackPart[]>()
  const doppelt = new Map<string, number>()

  for (const track of tage) {
    const datum = track.startedAt !== null ? isoDate(track.startedAt) : ''
    const bisher = gruppen.get(datum) ?? []
    for (const teil of trackParts(track)) {
      if (trackKonflikt(bisher, teil)) {
        doppelt.set(datum, (doppelt.get(datum) ?? 0) + 1)
        continue
      }
      bisher.push(teil)
    }
    gruppen.set(datum, bisher)
  }

  const entwuerfe: Tagesentwurf[] = []
  for (const [datum, teile] of gruppen) {
    const track = mergeTrackParts(teile)
    if (!track) continue
    entwuerfe.push({
      datum,
      track,
      von: rand(track.geometry, false),
      bis: rand(track.geometry, true),
      fehlstuecke: fehlstueckeVon(nachFahrtzeit(teile)),
      halte: halteUnterwegs(track),
      doppelt: doppelt.get(datum) ?? 0,
      aktiv: true,
    })
  }

  // Ohne Datum gibt es keine Stelle im Kalender. Solche Tage hängen sich hinten
  // an, statt vorne zu stehen: eine leere Zeichenkette sortiert sonst vor jedes
  // Datum, und die Reise begänne mit dem Stück, von dem am wenigsten bekannt ist.
  return entwuerfe.sort((a, b) => {
    if (a.datum === b.datum) return 0
    if (a.datum === '') return 1
    if (b.datum === '') return -1
    return a.datum < b.datum ? -1 : 1
  })
}

/** Was aus einer Auswahl von Dateien geworden ist, bevor angelegt wird. */
export interface Aufzeichnungslese {
  /** Ein Entwurf je Kalendertag, nach Datum sortiert. */
  entwuerfe: Tagesentwurf[]
  /** Die Namen der Dateien, aus denen die Entwürfe entstanden sind. */
  dateien: string[]
  /** Je halb gelesene Datei ein Satz - der Track daraus ist trotzdem dabei. */
  warnungen: string[]
  /** Die Dateien, aus denen gar nichts geworden ist. */
  fehler: Lesefehler[]
  /** Unbrauchbare Punkte über alle gelesenen Dateien, gezählt und nicht verloren. */
  dropped: number
}

/**
 * Liest die gewählten Dateien und macht daraus die Fahrtage.
 *
 * Gelesen wird mit leseDateien (mehrdateien.ts), also nach derselben Regel wie
 * in den beiden anderen Masken: eine Datei, aus der nichts wird, kostet nur
 * sich selbst und steht mit Namen und Grund in fehler; die übrigen laufen
 * durch. Das ist hier mehr wert als anderswo - diese Maske ist die, mit der
 * eine ganze Reise aus Aufzeichnungen entsteht, und dort liegen die meisten
 * Dateien auf einmal im Dialog. Wer vier wählt und eine davon ist unbrauchbar,
 * darf nicht alle vier verlieren.
 *
 * Zusammengelegt wird danach anders als in den beiden anderen Masken, und
 * deshalb steht dieser Schritt hier und nicht dort: was hier gilt, ist der
 * Kalendertag (tagesentwuerfe), und eine doppelt gewählte Datei fällt als Zahl
 * im Entwurf ihres Tages an (doppelt) und nicht als Zeile, die der Nutzer
 * abhaken müsste. Eine Liste von Kandidaten gibt es hier nicht: gewählt wird
 * der Tag, nicht die Datei.
 *
 * @param jetzt Ein Zeitpunkt für die ganze Auswahl und nicht einer je Datei:
 *   „gelesen am“ meint diesen Knopfdruck, und bei Aufzeichnungen ohne Uhr
 *   entscheidet er ausserdem über die Reihenfolge - die soll die der Auswahl
 *   bleiben (nachFahrtzeit in lib/gpx.ts).
 */
export async function leseAufzeichnungen(
  dateien: Iterable<Lesbar>,
  jetzt: number,
): Promise<Aufzeichnungslese> {
  const { gelesen, fehler } = await leseDateien(dateien, (xml, name) =>
    tracksByDay(xml, name, jetzt),
  )

  return {
    entwuerfe: tagesentwuerfe(gelesen.flatMap((g) => g.wert.tage)),
    dateien: gelesen.map((g) => g.name),
    warnungen: warnungenAus(gelesen),
    fehler,
    dropped: gelesen.reduce((summe, g) => summe + g.wert.dropped, 0),
  }
}

/**
 * Die Punkte der Kette: Start der Reise, danach je ein Ziel je Fahrtag.
 *
 * Aus dieser einen Liste entstehen alle Etappen, und deshalb kann die Kette
 * nicht reissen: Punkt i ist das Ziel der Etappe i und der Start der Etappe
 * i+1, ein und dasselbe Zahlenpaar.
 *
 * @param start Wo die erste erzeugte Etappe beginnt. In einer Reise mit Etappen
 *   ist das das Ziel der letzten von ihnen - dieselbe Regel wie beim Anlegen
 *   einer einzelnen Etappe (insertStageAfter in lib/trip.ts). Fehlt sie,
 *   beginnt die Reise dort, wo die Aufzeichnung beginnt.
 */
export function kettenpunkte(entwuerfe: Tagesentwurf[], start?: LatLon): LatLon[] {
  const gewaehlt = entwuerfe.filter((e) => e.aktiv)
  if (gewaehlt.length === 0) return []
  const erst = start ?? gewaehlt[0]!.von
  return [[erst[0], erst[1]], ...gewaehlt.map((e): LatLon => [e.bis[0], e.bis[1]])]
}

/**
 * Die Punkte, für die ein Name geholt werden soll - und sonst keine.
 *
 * Ein Ortsname kostet eine Anfrage an einen fremden Dienst, der eine je Sekunde
 * erlaubt. Eine Aufzeichnung im Sekundentakt hat zehntausende Punkte und ein
 * langer Tag ein Dutzend Halte; wer davon alles benennt, sitzt Minuten davor
 * und belastet einen Dienst, der davon lebt, dass niemand das tut.
 *
 * Gebraucht werden Namen an genau zwei Sorten von Punkten. Die Kette braucht
 * sie, weil aus ihnen Start und Ziel jeder Etappe werden - das sind bei n
 * Fahrtagen n+1 Punkte, für sechzehn Tage also siebzehn Anfragen und siebzehn
 * Sekunden. Und die Ränder eines Fehlstücks brauchen sie, wenn daraus
 * Zwischenziele werden sollen: „46.4337, 13.7469“ sagt niemandem, wo die
 * Aufzeichnung abgerissen ist.
 *
 * Die Halte unterwegs bekommen ausdrücklich keinen Namen. Sie stehen mit
 * Uhrzeit, Dauer und Koordinate in der Liste, und was sie waren, weiss der
 * Fahrer besser als jede Datenbank.
 */
export function benoetigteOrte(
  entwuerfe: Tagesentwurf[],
  start?: LatLon,
  mitFehlstuecken = true,
): LatLon[] {
  const gesehen = new Set<string>()
  const out: LatLon[] = []

  const merke = (at: LatLon) => {
    const key = ortsSchluessel(at)
    if (gesehen.has(key)) return
    gesehen.add(key)
    out.push(at)
  }

  for (const punkt of kettenpunkte(entwuerfe, start)) merke(punkt)
  if (mitFehlstuecken) {
    for (const entwurf of entwuerfe) {
      if (!entwurf.aktiv) continue
      for (const luecke of entwurf.fehlstuecke) {
        merke(luecke.von)
        merke(luecke.nach)
      }
    }
  }
  return out
}

/** Was aus den gewählten Tagen entsteht. */
export interface Erzeugnis {
  /** Die neuen Etappen, in Fahrreihenfolge. Ohne Tagesnummer - die vergibt renumber. */
  stages: Stage[]
  /** Was in das Tagebuch der jeweiligen Etappe geschrieben wird. */
  eintraege: { stageId: string; patch: Partial<DiaryEntry> }[]
}

/** Der Name eines Punktes, oder seine Koordinate, wenn keiner bekannt ist. */
function benennung(at: LatLon, namen: Ortsverzeichnis): string {
  // Die Koordinate und nicht „Ziel“: sechzehn Etappen, die alle „Ziel“ heissen,
  // sind im Buch und in jeder Auswahlliste ununterscheidbar. Die Koordinate ist
  // wenigstens die Wahrheit über das, was bekannt ist, und sie sieht so aus,
  // wie sie gemeint ist - als etwas, das noch niemand benannt hat.
  return namen[ortsSchluessel(at)]?.name ?? ortsSchluessel(at)
}

/** Das Länderkürzel einer Etappe, oder der Übergang, wenn sie eines wechselt. */
function landZwischen(von: LatLon, nach: LatLon, namen: Ortsverzeichnis): string {
  const a = namen[ortsSchluessel(von)]?.land ?? ''
  const b = namen[ortsSchluessel(nach)]?.land ?? ''
  if (a === b) return a
  if (a === '' || b === '') return a || b
  return `${a}→${b}`
}

/**
 * Die Zwischenziele einer erzeugten Etappe: die Ränder ihrer Fehlstücke.
 *
 * Ein Zwischenpunkt ist eine Vorgabe an die Routenberechnung (Waypoint in
 * types.ts), und genau darum geht es hier. Wo ein Stück Weg fehlt, zeichnet die
 * Karte heute eine Gerade zwischen zwei Punkten, die niemand so gefahren ist.
 * Mit den beiden Rändern als Zwischenziel lässt sich für die Etappe eine
 * Strecke berechnen, die dort entlangführt, wo eine Strasse ist - und die
 * Ansicht stellt berechnete und gefahrene Linie ohnehin nebeneinander. Was
 * dabei entsteht, behauptet nichts: es ist die geplante Linie, und als solche
 * ist sie beschriftet.
 *
 * Mehr als MAX_VIA nimmt der Routendienst nicht entgegen. Sind es mehr, bleiben
 * die ersten stehen - sie liegen am Anfang des Tages, und eine Etappe, deren
 * Anfang stimmt und deren Ende gerät, ist besser als eine, bei der beides gerät.
 */
function zwischenziele(entwurf: Tagesentwurf, namen: Ortsverzeichnis): Waypoint[] {
  const out: Waypoint[] = []
  for (const luecke of entwurf.fehlstuecke) {
    out.push({ name: benennung(luecke.von, namen), at: [luecke.von[0], luecke.von[1]] })
    out.push({ name: benennung(luecke.nach, namen), at: [luecke.nach[0], luecke.nach[1]] })
  }
  return out.slice(0, MAX_VIA)
}

/**
 * Macht aus den gewählten Fahrtagen Etappen und Tagebucheinträge.
 *
 * Die Kilometer stehen als Handwert in der Etappe und als realKm im Tagebuch -
 * derselbe Wert, den der Knopf unter einer einzelnen Aufzeichnung schreibt
 * (TrackPanel.tsx), und auf demselben Weg: auf Knopfdruck und nach Vorlage.
 * Gerechnet wird daraus nirgends weiter; track.distanceKm geht in keine Summe
 * ein und darf es nicht (siehe tripTotals in lib/trip.ts und die Anmerkung in
 * lib/fahrzeit.ts). Was hier entsteht, ist ein Eintrag und keine Rechnung.
 *
 * Die geplante Fahrzeit ist die gemessene. Das ist bei einer Etappe, die aus
 * ihrer eigenen Aufzeichnung entsteht, keine Schätzung, sondern die einzige
 * ehrliche Zahl - geplant hat hier niemand etwas.
 *
 * @param namen Was über die Punkte bekannt ist. Darf leer sein: dann heissen
 *   Start und Ziel nach ihrer Koordinate, und niemand hat einen Ort behauptet.
 */
export function baueEtappen(
  entwuerfe: Tagesentwurf[],
  start: LatLon | undefined,
  namen: Ortsverzeichnis,
  mitFehlstuecken: boolean,
): Erzeugnis {
  const gewaehlt = entwuerfe.filter((e) => e.aktiv)
  const punkte = kettenpunkte(entwuerfe, start)
  if (punkte.length === 0) return { stages: [], eintraege: [] }

  const stages: Stage[] = []
  const eintraege: Erzeugnis['eintraege'] = []

  gewaehlt.forEach((entwurf, i) => {
    const c1 = punkte[i]!
    const c2 = punkte[i + 1]!
    const km = Math.round(entwurf.track.distanceKm)
    const via = mitFehlstuecken ? zwischenziele(entwurf, namen) : []

    const stage: Stage = {
      id: newId('s'),
      // Die Nummer vergibt renumber beim Einhängen: sie ist die Stelle in der
      // Liste der ganzen Reise und nicht die in dieser Datei.
      day: 0,
      from: benennung(c1, namen),
      to: benennung(c2, namen),
      // Kopien und keine geteilten Verweise: c2 der einen und c1 der nächsten
      // Etappe sind derselbe Ort, aber nicht dasselbe Feld. Wer eines von
      // beiden verschiebt, verschiebt nicht stillschweigend das andere mit.
      c1: [c1[0], c1[1]],
      c2: [c2[0], c2[1]],
      ...(via.length > 0 ? { via } : {}),
      km,
      h: Math.round((entwurf.track.movingSeconds / 3600) * 100) / 100,
      land: landZwischen(c1, c2, namen),
      type: km === 0 ? 'standtag' : km >= LANGE_ETAPPE_KM ? 'lang' : 'kurz',
      camping: [],
      highlights: [],
      night: [],
      tip: '',
    }

    stages.push(stage)
    eintraege.push({
      stageId: stage.id,
      patch: {
        done: true,
        ...(entwurf.datum ? { date: entwurf.datum } : {}),
        realKm: String(km),
        track: entwurf.track,
      },
    })
  })

  return { stages, eintraege }
}

/**
 * Wie weit die Aufzeichnung eines Tages von der Stelle entfernt beginnt, an der
 * seine Etappe beginnt.
 *
 * Die beiden fallen auseinander, wo die Kette etwas anderes sagt als die Datei:
 * die Etappe beginnt am Ziel der vorherigen - beim ersten Tag also am Ziel der
 * letzten bestehenden Etappe der Reise -, die Aufzeichnung beginnt dort, wo das
 * Gerät angegangen ist. Über Nacht sind das ein paar Meter auf demselben Hof;
 * es können aber auch fünfzig Kilometer sein, wenn zwischendurch gefahren und
 * nicht aufgezeichnet wurde.
 *
 * Angeglichen wird deswegen nichts. Die Kette gewinnt, und der Abstand ist eine
 * Auskunft: was der Nutzer geplant hat, wird nicht verschoben, und was er
 * aufgezeichnet hat, wird nicht verworfen. Dieselbe Haltung wie bei
 * startHerkunft in lib/trip.ts - dort steht der Unterschied auch nur da, und
 * drücken muss ein Mensch.
 *
 * @returns Der Abstand in Metern je Stelle in der Liste, und nur für die Tage,
 *   die mitgehen. Was unter FEHLSTUECK_AB_M liegt, steht gar nicht erst darin:
 *   darüber ein Wort zu verlieren, machte das Verfahren unbrauchbar.
 */
export function anschlussAbstaende(
  entwuerfe: Tagesentwurf[],
  start?: LatLon,
): Record<number, number> {
  const out: Record<number, number> = {}
  let vorher = start
  entwuerfe.forEach((entwurf, i) => {
    if (!entwurf.aktiv) return
    if (vorher) {
      const meter = Math.round(haversineM(vorher, entwurf.von))
      if (meter >= FEHLSTUECK_AB_M) out[i] = meter
    }
    vorher = entwurf.bis
  })
  return out
}
